import moment from "moment";
import useSWR from "swr";

import * as endpointTypes from "./types";
import { Transaction } from "./types";

import {
  postThroughAuthProxy,
  fetcherThroughAuthProxy,
  fileFetcherThroughAuthProxy,
  deleteThroughAuthProxy,
} from "~/endpoints/utils";
import { TransactionWrap } from "~/hooks/use-transaction";

export const useTxList = (params: endpointTypes.TransactionListParameters | null) => {
  // @ts-expect-error couldn't make ts happy with the URLSearchParams
  const urlParams = new URLSearchParams(params).toString();
  const txListUrl = `tx/list?${urlParams}`;

  const shouldFetch = params?.vaspDID ? true : false;
  type TxInfoApiReturn = {
    transactions: Transaction[];
    paginateInfo: {
      totalItems: number;
      itemCount: number;
      itemsPerPage: number;
      totalPages: number;
      currentPage: number;
    };
  };
  const { data, error, mutate } = useSWR<TxInfoApiReturn>(shouldFetch ? txListUrl : null, fetcherThroughAuthProxy, {
    refreshInterval: 0,
  });

  return {
    data: {
      paginateInfo: data?.paginateInfo,
      transactions: data?.transactions.map((tx: Transaction) => new TransactionWrap(tx)),
    },
    mutate,
    isLoading: !error && !data,
    isError: error,
  };
};

/** Gets the transaction details info for NotaBene customers.  */
export const useTxInfo = (transactionId: string | null) => {
  const txInfoURL = `tx/info?id=${transactionId}`;

  const shouldFetch = transactionId ? true : false;

  const { data, error, mutate } = useSWR<Transaction>(shouldFetch ? txInfoURL : null, fetcherThroughAuthProxy, {
    refreshInterval: 0,
  });

  return {
    data: data ? new TransactionWrap(data) : undefined,
    mutate,
    isLoading: !error && !data && !!transactionId,
    error: error,
  };
};

/**
 * In this case, the payload goes in the query params like {{baseUrl}}/tx/approve?id=330bf23e-24e8-46ab-ae7e-6d27b0786e1f
 */
export const postTxApprove = (transactionId: string): Promise<Transaction> => {
  const urlParams = new URLSearchParams({ id: transactionId }).toString();
  const txApproveUrl = `tx/approve?${urlParams}`;
  return postThroughAuthProxy(txApproveUrl);
};

export const postTxCancel = (payload: endpointTypes.TransactionCancelRequest) => {
  const txCancelUrl = `tx/cancel`;
  return postThroughAuthProxy(txCancelUrl, { payload });
};

export const postTxReject = (payload: endpointTypes.TransactionRejectRequest) => {
  const txRejectUrl = `tx/reject`;
  return postThroughAuthProxy(txRejectUrl, { payload });
};

export const postTxNotReady = (payload: endpointTypes.TransactionRejectRequest) => {
  const txNotReadyUrl = `tx/notReady`;
  return postThroughAuthProxy(txNotReadyUrl, { payload });
};

export const postTxDecline = (payload: endpointTypes.TransactionDeclineRequest) => {
  const txDeclineUrl = `tx/decline`;
  return postThroughAuthProxy(txDeclineUrl, { payload });
};

export const postTxConfirm = (transactionId: string): Promise<Transaction> => {
  //NOTE: in this case, the payload goes in the query params like {{baseUrl}}/tx/confirm?id=330bf23e-24e8-46ab-ae7e-6d27b0786e1
  const urlParams = new URLSearchParams({ id: transactionId }).toString();
  const txConfirmUrl = `tx/confirm?${urlParams}`;
  return postThroughAuthProxy(txConfirmUrl);
};

export const postTxAccept = (transactionId: string) => {
  //NOTE: in this case, the payload goes in the query params like {{baseUrl}}/tx/accept?id=330bf23e-24e8-46ab-ae7e-6d27b0786e1f
  const urlParams = new URLSearchParams({ id: transactionId }).toString();
  const txAcceptUrl = `tx/accept?${urlParams}`;
  return postThroughAuthProxy(txAcceptUrl);
};

export const postTxBulk = (payload: endpointTypes.TransactionBulkRequest): Promise<Transaction[]> => {
  const txBulkUrl = `tx/bulk`;
  return postThroughAuthProxy(txBulkUrl, { payload });
};

export const postTxCreate = (payload: endpointTypes.TransactionCreateRequest) => {
  const txCreateUrl = `tx/create`;
  return postThroughAuthProxy(txCreateUrl, { payload });
};

export const postTxValidate = (payload: endpointTypes.TransactionValidateRequest) => {
  const txValidateUrl = `tx/validate`;
  return postThroughAuthProxy(txValidateUrl, { payload });
};

export const postTxValidateFull = (payload: endpointTypes.TransactionCreateRequest) => {
  const txValidateFullUrl = `tx/validate/full`;
  return postThroughAuthProxy(txValidateFullUrl, { payload });
};

export const postTxUpdate = (payload: endpointTypes.TransactionUpdateRequest) => {
  const txUpdateUrl = `tx/update`;
  return postThroughAuthProxy(txUpdateUrl, { payload });
};

export const useTransactionExportRequests = (params: {
  vaspDid: string;
  statuses?: endpointTypes.TransactionExportStatus[];
  createdBy?: string[] | string;
  perPage?: number;
  page?: number;
}) => {
  const { vaspDid, statuses, createdBy, perPage = 100, page } = params;
  // @ts-expect-error couldn't make ts happy with the URLSearchParams
  const urlParams = new URLSearchParams({
    vasp_did: vaspDid,
    created_by: createdBy,
    per_page: perPage,
    ...(statuses && { statuses }),
    ...(page && { page }),
  }).toString();
  const url = `service/transactions/export-requests?${urlParams}`;

  const { data, error, mutate, isLoading } = useSWR<endpointTypes.TransactionExportRequestsResponse>(
    url,
    fetcherThroughAuthProxy,
    { refreshInterval: 10000 },
  );

  return {
    data,
    mutate,
    isLoading,
    error,
  };
};

export const getTransactionExportRequests = async (params: {
  vaspDid: string;
  createdBy?: string[] | string;
  perPage?: number;
  page?: number;
  statuses?: string[];
}): Promise<endpointTypes.TransactionExportRequestsResponse> => {
  const { vaspDid, createdBy, perPage = 100, page, statuses } = params;

  // @ts-expect-error couldn't make ts happy with the URLSearchParams
  const urlParams = new URLSearchParams({
    vasp_did: vaspDid,
    created_by: createdBy,
    per_page: perPage,
    ...(page && { page }),
    ...(statuses && { statuses }),
  }).toString();
  const url = `service/transactions/export-requests?${urlParams}`;

  const response = await fetcherThroughAuthProxy(url);
  return response;
};

export const getTransactionExportFile = async (exportId: string, vaspDid: string) => {
  const url = `service/transactions/export-requests/${exportId}?vasp_did=${vaspDid}`;

  const response = await fileFetcherThroughAuthProxy(url);

  const match = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "");
  const filename = match ? match[1] : `${moment().format("YYYY-MM-DD")}_transfers-export.csv`;

  return {
    filename,
    file: await response.blob(),
  };
};

export const postTransactionExportRequests = (
  payload: endpointTypes.TransactionExportRequestCreateRequest,
): Promise<endpointTypes.TransactionExportRequestCreateResponse> => {
  const url = `service/transactions/export-requests`;
  return postThroughAuthProxy(url, { payload });
};

export const deleteTransactionExportRequests = (id: string, vaspDID: string) => {
  return deleteThroughAuthProxy(`service/transactions/export-requests/${id}?vasp_did=${vaspDID}`);
};

export const postTxRedirect = (payload: endpointTypes.TransactionRedirectRequest) => {
  const txRedirectUrl = `tx/redirect?id=${payload.id}&to=${payload.to}`;
  return postThroughAuthProxy(txRedirectUrl);
};

export const postTxBulkRedirect = (payload: endpointTypes.TransactionBulkRedirectRequest) => {
  const txRedirectUrl = `v1/tx/bulk-redirect`;
  return postThroughAuthProxy(txRedirectUrl, { payload });
};
